/**
 * Keyboard shortcuts for the toolbar.
 *
 * Handles Ctrl+Z (undo), Ctrl+Shift+Z / Cmd+Y (redo),
 * 'A' (toggle panel), and Escape (close panel).
 */

import { useEffect } from "react";
import { useSessionHistoryStore } from "@/stores/sessionHistoryStore";
import { isEditableTarget } from "@/utils/input";
import { isPrimaryMod } from "@/utils/input";

export interface UseToolbarShortcutsParams {
  isPanelExpanded: boolean;
  setIsPanelExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  disableCommands?: boolean;
}

export function useToolbarShortcuts({
  isPanelExpanded,
  setIsPanelExpanded,
  disableCommands = false,
}: UseToolbarShortcutsParams) {
  const canUndo = useSessionHistoryStore((state) => state.canUndo());
  const canRedo = useSessionHistoryStore((state) => state.canRedo());
  const undo = useSessionHistoryStore((state) => state.undo);
  const redo = useSessionHistoryStore((state) => state.redo);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Never hijack any toolbar shortcut during IME composition —
      // Escape in particular needs to reach the browser so it can
      // cancel the in-progress composition.
      if (e.isComposing) return;

      // Normalize the key for shifted letters (Shift+Z yields "Z" on
      // most layouts; Shift+A yields "A"). We compare the lowercase
      // form throughout.
      const key = e.key.toLowerCase();
      // Platform-aware: ⌘ on Mac, Ctrl elsewhere — rejects the wrong
      // modifier so e.g. Ctrl+Z on Mac no longer triggers canvas undo
      // (the user would expect it to do nothing or to behave as the
      // native Ctrl+Z in any focused editor).
      const isMod = isPrimaryMod(e);

      // When focus is in an editable surface, only the Escape→close-panel
      // exception fires. Every other toolbar shortcut defers to native
      // text behavior (text undo, find-in-page, etc.).
      if (isEditableTarget(e.target)) {
        if (e.key === "Escape" && isPanelExpanded) {
          e.preventDefault();
          setIsPanelExpanded(false);
        }
        return;
      }

      if (key === "z" && isMod && !e.shiftKey) {
        if (!canUndo || disableCommands) return;
        // We own Ctrl/Cmd+Z on the canvas — suppress the browser's
        // default (a no-op outside text fields, but block it anyway)
        // before checking for held-key repeats.
        e.preventDefault();
        if (e.repeat) return;
        undo();
        return;
      }

      if ((key === "z" && isMod && e.shiftKey) || (key === "y" && isMod)) {
        if (!canRedo || disableCommands) return;
        e.preventDefault();
        if (e.repeat) return;
        redo();
        return;
      }

      if (key === "a" && !isMod) {
        if (disableCommands) return;
        e.preventDefault();
        if (e.repeat) return;
        setIsPanelExpanded((prev) => !prev);
        return;
      }

      if (e.key === "Escape" && isPanelExpanded) {
        e.preventDefault();
        setIsPanelExpanded(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isPanelExpanded, canUndo, canRedo, undo, redo, disableCommands, setIsPanelExpanded]);
}
